import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { ServerError } from '../../models';
import type { GetReferralOfferResponse, GetSignupReferralResponse, ReferralOfferResult } from '../../models/reqres';
import { exchangeApi, handleErrorCallback, noop } from '../core';

/** Maps the V2 offer response into the normalized shape the referral store consumes. */
function mapReferralOffer(data: GetReferralOfferResponse): ReferralOfferResult {
    // `valid` is null/undefined when the backend call failed — surface as unknown, not invalid.
    const valid = data.valid ?? undefined;
    if (valid !== true || !data.referee) {
        return { valid, isVariable: false };
    }

    const referee = data.referee;
    const isVariable = referee.reward_type === 'variable';
    const rewardDetails = referee.reward_details;
    return {
        valid: true,
        referrerFirstName: data.referrer?.display_name ?? undefined,
        refereeCreditCents: rewardDetails?.flat_reward?.payout_amount_cents,
        tradeThresholdCents: referee.trade_threshold_cents ?? undefined,
        maxReferralRewardCents: rewardDetails?.variable_reward?.max_reward_cents,
        isVariable,
    };
}

/** V2 pre-signup referral preview: separates flat vs variable rewards and exposes the trade threshold. */
export function getReferralOffer(
    referralCode: string,
    onSuc: (result: ReferralOfferResult) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetReferralOfferResponse>(`/rewards/referrals/offer/${encodeURIComponent(referralCode)}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(mapReferralOffer(response.data));
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getSignupReferral(
    userId: string,
    onSuc: (referralInfos: GetSignupReferralResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetSignupReferralResponse>(`/users/${userId}/referral_code`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
